import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { STALE_TIMES } from "@/lib/query-performance";
import { isProblemType } from "@iamu/shared";
import { compareFindingsByCriticality } from "@/components/experience/vocabulary";
import { findingTitleMap } from "@/components/walkthrough/reasoning-text";
import { useWorkspaceRuns } from "./use-workspace-runs";
import { useActiveFinding } from "./use-active-finding";
import type { WalkthroughFinding, FindingKind } from "./workspace-types";

function isSetAside(status: string): boolean {
  return (
    status === "dismissed" || status === "wont_fix" || status === "deferred"
  );
}

function matchesKind(type: string, kind: FindingKind): boolean {
  if (kind === "all") return true;
  if (kind === "problem") return isProblemType(type);
  if (kind === "strength") return type === "helps";
  return true;
}

interface UseWalkthroughWorkspaceDataOptions {
  projectId: string | undefined;
  walkthroughId: string | undefined;
  isValidId: boolean;
  baseRunId: string | null;
  selectedFindingId: string | null;
  stepNumberFromUrl: number | null;
  statusFilter: string;
  hasExplicitStatusFilter?: boolean;
  kindFilter: FindingKind;
  severityFilter: string;
}

export function useWalkthroughWorkspaceData({
  projectId,
  walkthroughId,
  isValidId,
  baseRunId,
  selectedFindingId,
  stepNumberFromUrl,
  statusFilter,
  hasExplicitStatusFilter = false,
  kindFilter,
  severityFilter,
}: UseWalkthroughWorkspaceDataOptions) {
  const {
    data: walkthrough,
    isLoading,
    error,
    refetch,
  } = trpc.walkthrough.getById.useQuery(
    { id: walkthroughId ?? "" },
    {
      enabled: isValidId,
      refetchInterval: (query) => {
        const s = query.state.data?.status;
        return s === "running" || s === "pending" ? 3000 : false;
      },
    },
  );

  const scenarioIdForRuns = walkthrough?.scenario?.id ?? "";
  const { data: scenarioRuns } = trpc.walkthrough.list.useQuery(
    { scenarioId: scenarioIdForRuns },
    { enabled: !!scenarioIdForRuns, staleTime: STALE_TIMES.list },
  );

  const { data: guidanceRules } = trpc.guidance.list.useQuery(
    { projectId: projectId ?? "", status: "active" },
    { enabled: !!projectId },
  );

  const { data: projectContext } = trpc.project.getContext.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId, staleTime: STALE_TIMES.detail },
  );

  const { data: trackerTarget } = trpc.workflow.integrationTarget.get.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId, staleTime: 60_000 },
  );

  const { runNumber, runsChronological, defaultBaseRunId, activeBaseRunId } =
    useWorkspaceRuns({
      walkthrough,
      scenarioRuns,
      walkthroughId,
      baseRunId,
    });

  const allFindings = useMemo(() => {
    const raw = (walkthrough?.findings ?? []) as WalkthroughFinding[];
    const reportCauses = walkthrough?.designerReview?.designCauses ?? [];
    return raw
      .filter((f) => !f.designCauseOf)
      .map((f) => {
        const rowCauses = raw
          .filter((other) => other.designCauseOf === f.id)
          .map((c) => ({
            id: c.id,
            title: c.title,
            severity: c.severity,
            what: c.what,
            why: c.why,
            basis: c.basis,
            screen: c.screen,
            breaksRule: c.breaksRule,
            breaksFile: c.breaksFile,
            description: c.description,
          }));
        const existing = f.designCauses ?? rowCauses;
        if (existing.length > 0) {
          return { ...f, designCauses: existing };
        }
        const matchedReportCauses = f.title
          ? reportCauses
              .filter((rc) => rc.finding === f.title)
              .map((rc) => ({
                code: rc.code ?? null,
                severity: rc.severity ?? null,
                breaksRule: rc.breaksRule ?? null,
                detail: rc.detail ?? rc.markdown,
              }))
          : [];
        return matchedReportCauses.length > 0
          ? { ...f, designCauses: matchedReportCauses }
          : f;
      });
  }, [walkthrough?.findings, walkthrough?.designerReview?.designCauses]);

  const { activeFinding, activeStep, setSelectedDeltaItem } = useActiveFinding({
    walkthrough,
    allFindings,
    selectedFindingId,
    stepNumberFromUrl,
  });

  const problemsCount = allFindings.filter((f) => isProblemType(f.type)).length;
  const helpsCount = allFindings.filter((f) => f.type === "helps").length;
  const openCount = allFindings.filter(
    (f) => isProblemType(f.type) && f.triageStatus === "open",
  ).length;

  const effectiveStatusFilter =
    !hasExplicitStatusFilter &&
    statusFilter === "open" &&
    openCount === 0 &&
    allFindings.length > 0
      ? "all"
      : statusFilter;

  const filteredFindings = useMemo(() => {
    return allFindings
      .filter((f) => {
        if (!matchesKind(f.type, kindFilter)) return false;
        // Strengths are positive observations, not triageable problems with severities
        if (f.type === "helps") {
          return true;
        }
        if (effectiveStatusFilter !== "all") {
          const fStatus = f.triageStatus;
          if (effectiveStatusFilter === "open" && fStatus !== "open")
            return false;
          if (effectiveStatusFilter === "addressed" && fStatus !== "addressed")
            return false;
          if (effectiveStatusFilter === "dismissed" && !isSetAside(fStatus))
            return false;
        }
        if (severityFilter !== "all" && f.severity !== severityFilter)
          return false;
        return true;
      })
      .sort(compareFindingsByCriticality);
  }, [allFindings, effectiveStatusFilter, kindFilter, severityFilter]);

  const walkthroughSteps = (walkthrough?.steps ?? []) as Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
    pageUrl?: string | null;
    surface?: string | null;
  }>;

  const htmlReport = walkthrough?.artifacts?.find(
    (a: any) => a.type === "html_report",
  );
  const whatWorked = Array.isArray(walkthrough?.whatWorked)
    ? (walkthrough.whatWorked as string[])
    : [];
  const openQuestions = Array.isArray(walkthrough?.openQuestions)
    ? (walkthrough.openQuestions as string[])
    : [];
  const findingTitles = findingTitleMap(allFindings);
  const verdicts = walkthrough?.verdict as
    | Array<{ question: string; rating: string; reasoning?: string }>
    | undefined;
  const canonicalIds = new Set(
    allFindings.map((f) => f.canonicalFindingId).filter(Boolean),
  );
  const thisWalkthroughGuidance = (guidanceRules ?? []).filter((g) =>
    g.sourceCanonicalIds.some((id: string) => canonicalIds.has(id)),
  );

  return {
    walkthrough,
    isLoading,
    error,
    refetch,
    scenarioRuns,
    guidanceRules: thisWalkthroughGuidance,
    projectContext,
    trackerTarget,
    runNumber,
    runsChronological,
    defaultBaseRunId,
    activeBaseRunId,
    allFindings,
    filteredFindings,
    effectiveStatusFilter,
    activeFinding,
    activeStep,
    setSelectedDeltaItem,
    walkthroughSteps,
    htmlReport,
    whatWorked,
    openQuestions,
    findingTitles,
    verdicts,
    problemsCount,
    helpsCount,
    openCount,
  };
}
